/**
 * SourceGroupPicker — controlled select for the 7 canonical SOURCE_GROUPS.
 *
 * Supports null/"unset" as an explicit option (maps to null in the API).
 * Labels driven by i18n keys completeness.group.* (declared in P4 locale files).
 *
 * Accessibility:
 *   - <label> associated via htmlFor/id
 *   - select is keyboard-navigable natively
 *
 * GH-235 P5.
 */

import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import { useAppStore } from '../../../stores/app-store'
import { SOURCE_GROUPS } from '@agentwiki/shared'
import type { SourceGroup } from '@agentwiki/shared'

interface SourceGroupPickerProps {
  /** Current value; null = untagged/unset. */
  value: SourceGroup | null
  onChange: (value: SourceGroup | null) => void
  /** Whether a mutation is in-flight for this row. */
  isLoading?: boolean
  /** Whether the picker is disabled (e.g. during optimistic update or error state). */
  disabled?: boolean
  className?: string
}

const UNSET_VALUE = '__unset__'

export function SourceGroupPicker({
  value,
  onChange,
  isLoading = false,
  disabled = false,
  className,
}: SourceGroupPickerProps) {
  const id = useId()
  const { t } = useTranslation('packages')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const raw = e.target.value
    const newValue = raw === UNSET_VALUE ? null : (raw as SourceGroup)
    // Guard: skip no-op selections to prevent spurious mutation round-trips
    if (newValue === value) return
    onChange(newValue)
  }

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <label
        htmlFor={id}
        className={cn(
          'text-[0.625rem] font-semibold uppercase tracking-wide',
          isDark ? 'text-neutral-500' : 'text-neutral-400',
        )}
      >
        {t('sourceGroup.picker.label')}
      </label>

      <select
        id={id}
        value={value ?? UNSET_VALUE}
        onChange={handleChange}
        disabled={disabled || isLoading}
        aria-label={t('sourceGroup.picker.label')}
        aria-busy={isLoading}
        className={cn(
          'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-brand-500/50',
          isDark
            ? 'border-white/[0.10] bg-surface-2 text-neutral-200 disabled:opacity-50'
            : 'border-neutral-200 bg-white text-neutral-800 disabled:opacity-50',
          isLoading && 'cursor-wait',
        )}
      >
        <option value={UNSET_VALUE}>{t('sourceGroup.picker.placeholder')}</option>
        {SOURCE_GROUPS.map((group) => (
          <option key={group} value={group}>
            {t(`completeness.group.${group}`)}
          </option>
        ))}
      </select>
    </div>
  )
}
